import {Component} from '@angular/core';

import {GeneralListComponent} from '../../general/list/generalList.component';
import {ActivatedRoute, Router} from '@angular/router';
import {Tenant} from '../../../interfaces/tenant';
import {TenantService} from '../../../services/tenant.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ButtonMeta} from '../../common/buttons/buttonMeta';
import {NgbModalRef} from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import {ModalWindowComponent} from '../../common/modal-window/modal-window';
import {ServiceAnswer, ServiceAnswerAny} from '../../../../shared/service-answer';
import {TenantCacheService} from '../../../services/tenant-cache.service';
import { MessageService } from '../../../../shared/message.service';

/**
 * Tenant list view.
 */
@Component({
  selector: 'qm-tenants',
  templateUrl: './tenants.component.html',
  styleUrls: ['./tenants.component.css']
})
export class TenantsComponent extends GeneralListComponent<Tenant> {

  private static CONTROL_BUTTON_ID_DELETE_CACHE = 'CONTROL_BUTTON_ID_DELETE_CACHE';

  constructor(
    private tenantService: TenantService,
    private tenantCacheService: TenantCacheService,
    router: Router,
    messagesService: MessageService,
    modalService: NgbModal,
    route: ActivatedRoute) {
    super(
      tenantService,
      router,
      messagesService,
      route,
      modalService,
      {
        entityNameSingular: 'tenant',
        entityNamePlural: 'tenants',
        formUrl: 'tenant',
        listUrl: 'tenants'
      }
    );
  }

  /**
   * Opens a dialog that confirms if the cache of the selected tenant should be removed.
   * If user presses yes button then the cache is removed.
   */
  private openCacheRemoveDialog(): void {
    const selected: Tenant = this.selectedEntity;
    const id: number = +this.getIdOfEntity(selected);
    const name: string = selected['name'];
    const ngbModalRef: NgbModalRef = this.modalService.open(ModalWindowComponent);
    this.increaseOpenView();
    ngbModalRef.componentInstance.title = `Delete cache of the tenant ${name} (${id ? id : 'unknown id'})?`;
    ngbModalRef.componentInstance.text = `Are you sure that you want to
    delete the cache of the tenant ${name} (${id})? The tenant won't be deleted.`;

    ngbModalRef.componentInstance.buttons = [
      {text: 'Cancel', id: 'cancel', focus: true},
      {text: 'Delete', id: 'delete'},
    ];
    ngbModalRef.componentInstance.closed = async (buttonId: string) => {
      ngbModalRef.close();
      if (buttonId === 'delete') {
        try {
          const serviceAnswer: ServiceAnswerAny = await this.tenantCacheService.removeCache(id);
          if (serviceAnswer.isOk) {
            this.messagesService.addSuccess(`Successfully deleted the cache of the tenant ${name} (${id})`);
            return;
          } else {
            const errorMessage = `Deleting the cache of the tenant ${name} (${id}) failed. Backend returned \
            HTTP code ${serviceAnswer.errorCode}. Error message: ${serviceAnswer.errorMessage}`;
            console.error(errorMessage);
            this.messagesService.addDanger(errorMessage);
          }

        } catch (error) {
          const message = `Unexpected error when deleting the cache of the tenant ${id}: ${JSON.stringify(error)}`;
          console.error(message);
          this.messagesService.addDanger(message);
        }
      }
    };

    ngbModalRef.result.then((result) => {
      this.decreaseOpenView();
    }, (reason) => {
      this.decreaseOpenView();
    });
  }

  /**
   * When a control button has been pressed. Call super controlButtonPressed and
   * checks if the cache delete button has been pressed.
   * @param buttonId The id of the button.
   */
  controlButtonPressed(buttonId: string) {
    super.controlButtonPressed(buttonId);
    if (buttonId === TenantsComponent.CONTROL_BUTTON_ID_DELETE_CACHE) {
      this.openCacheRemoveDialog();
    }
  }

  /**
   * Returns array of button meta objects. These objects are used to create buttons.
   */
  public getButtonsMeta(): ButtonMeta[] {
    const buttonMeta: ButtonMeta[] = super.getButtonsMeta();
    buttonMeta.splice(2, 0,
      {
        disableWhenNoSelection: true,
        text: 'Delete cache',
        type: 'general',
        id: TenantsComponent.CONTROL_BUTTON_ID_DELETE_CACHE
      });
    return buttonMeta;
  }
}
